import React, { useState, useEffect } from 'react';
import { teacherAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ChartComponent from '../../components/common/ChartComponent';

const SubjectRankings = () => {
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [academicSessions, setAcademicSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [rankings, setRankings] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [performanceAnalysis, setPerformanceAnalysis] = useState(null);

  useEffect(() => {
    fetchAssignedSubjects();
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    if (selectedSubject && selectedClass && selectedSession && selectedTerm) {
      fetchSubjectRankings();
      fetchPerformanceAnalysis();
    }
  }, [selectedSubject, selectedClass, selectedSession, selectedTerm]);

  const fetchAssignedSubjects = async () => {
    try {
      const response = await teacherAPI.getAssignedSubjects();
      setAssignedSubjects(response.data);
    } catch (error) {
      toast.error('Failed to load assigned subjects');
    }
  };

  const fetchAcademicSessions = async () => {
    try {
      const response = await teacherAPI.getAcademicSessions();
      setAcademicSessions(response.data);
    } catch (error) {
      toast.error('Failed to load academic sessions');
    }
  };

  const fetchSubjectRankings = async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getSubjectRankings(
        selectedClass,
        selectedSubject,
        selectedSession,
        selectedTerm
      );
      
      setRankings(response.data.rankings);
      setStatistics(response.data.statistics);
    } catch (error) {
      toast.error('Failed to load subject rankings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceAnalysis = async () => {
    try {
      const response = await teacherAPI.getClassPerformanceAnalysis(
        selectedClass,
        selectedSubject,
        selectedSession,
        selectedTerm
      );
      setPerformanceAnalysis(response.data);
    } catch (error) {
      console.error('Failed to load performance analysis:', error);
    }
  };

  const getSubjectName = () => {
    const subject = assignedSubjects.find(s => s.subject_id == selectedSubject);
    return subject ? subject.subject_name : 'Unknown Subject';
  };

  const getClassName = () => {
    const subject = assignedSubjects.find(s => 
      s.subject_id == selectedSubject && s.class_id == selectedClass
    );
    return subject ? subject.class_name : 'Unknown Class';
  };

  const getSessionName = () => {
    const session = academicSessions.find(s => s.id == selectedSession);
    return session ? session.academic_session : 'Unknown Session';
  };

  const getTermName = () => {
    if (!selectedSession) return 'Unknown Term';
    const session = academicSessions.find(s => s.id == selectedSession);
    if (session) {
      const term = session.terms.find(t => t.id == selectedTerm);
      return term ? term.term : 'Unknown Term';
    }
    return 'Unknown Term';
  };

  const getGradeColor = (grade) => {
    if (grade === 'A+') return 'bg-gradient-to-r from-green-100 to-green-50 border border-green-200';
    if (grade.startsWith('A')) return 'bg-green-50 border border-green-200';
    if (grade.startsWith('B')) return 'bg-blue-50 border border-blue-200';
    if (grade.startsWith('C')) return 'bg-yellow-50 border border-yellow-200';
    if (grade.startsWith('D')) return 'bg-orange-50 border border-orange-200';
    return 'bg-red-50 border border-red-200';
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-100 to-yellow-50 border border-yellow-300';
    if (rank === 2) return 'bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-300';
    if (rank === 3) return 'bg-gradient-to-r from-orange-100 to-orange-50 border border-orange-200';
    if (rank <= 10) return 'bg-blue-50 border border-blue-200';
    return 'bg-white border border-gray-200';
  };

  const getGradeDistributionChartData = () => {
    if (!statistics || !statistics.grade_distribution) return null;

    const labels = Object.keys(statistics.grade_distribution);
    const data = Object.values(statistics.grade_distribution);

    return {
      labels,
      datasets: [
        {
          label: 'Percentage of Students',
          data,
          backgroundColor: [
            'rgba(34, 197, 94, 0.7)',  // A+ - Green
            'rgba(34, 197, 94, 0.5)',  // A - Light Green
            'rgba(59, 130, 246, 0.5)', // B - Blue
            'rgba(250, 204, 21, 0.5)', // C - Yellow
            'rgba(249, 115, 22, 0.5)', // D - Orange
            'rgba(239, 68, 68, 0.5)'   // F - Red
          ],
          borderColor: [
            'rgb(34, 197, 94)',
            'rgb(34, 197, 94)',
            'rgb(59, 130, 246)',
            'rgb(250, 204, 21)',
            'rgb(249, 115, 22)',
            'rgb(239, 68, 68)'
          ],
          borderWidth: 1
        }
      ]
    };
  };

  const exportToCSV = () => {
    if (rankings.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Rank', 'Registration No.', 'Student Name', 'Total Score', 'Grade', 'Percentage'];
    const csvContent = [
      headers.join(','),
      ...rankings.map(r => [
        r.rank,
        `"${r.student_reg}"`,
        `"${r.student_name}"`,
        r.total_score,
        r.grade,
        r.percentage
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `subject_rankings_${getSubjectName()}_${getClassName()}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Rankings exported to CSV');
  };

  const printRankings = () => {
    window.print();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Subject Rankings</h1>
          <p className="text-gray-600">View student rankings by subject and class</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={exportToCSV}
            disabled={rankings.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <i className="fas fa-file-export mr-2"></i>
            Export CSV
          </button>
          <button
            onClick={printRankings}
            disabled={rankings.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <i className="fas fa-print mr-2"></i>
            Print
          </button>
        </div>
      </div>

      {/* Selection Panel */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Subject *
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => {
                setSelectedSubject(e.target.value);
                setSelectedClass('');
                setRankings([]);
              }}
              className="input-field"
              disabled={loading}
            >
              <option value="">Choose a subject</option>
              {assignedSubjects.map((subject) => (
                <option key={subject.subject_id} value={subject.subject_id}>
                  {subject.subject_name} ({subject.subject_code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Class *
            </label>
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setRankings([]);
              }}
              className="input-field"
              disabled={!selectedSubject || loading}
            >
              <option value="">Choose a class</option>
              {assignedSubjects
                .filter(s => s.subject_id == selectedSubject)
                .map((subject) => (
                  <option key={subject.class_id} value={subject.class_id}>
                    {subject.class_name}
                  </option>
                ))
              }
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Academic Session *
            </label>
            <select
              value={selectedSession}
              onChange={(e) => {
                setSelectedSession(e.target.value);
                setSelectedTerm('');
                setRankings([]);
              }}
              className="input-field"
              disabled={!selectedClass || loading}
            >
              <option value="">Choose session</option>
              {academicSessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.academic_session}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Term *
            </label>
            <select
              value={selectedTerm}
              onChange={(e) => {
                setSelectedTerm(e.target.value);
                setRankings([]);
              }}
              className="input-field"
              disabled={!selectedSession || loading}
            >
              <option value="">Choose term</option>
              {selectedSession && academicSessions
                .find(s => s.id == selectedSession)
                ?.terms.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.term}
                  </option>
                ))
              }
            </select>
          </div>
        </div>

        {selectedSubject && selectedClass && selectedSession && selectedTerm && (
          <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-purple-800">
                  {getSubjectName()} Rankings - {getClassName()}
                </h3>
                <p className="text-sm text-purple-600">
                  {getSessionName()} - {getTermName()}
                </p>
              </div>
              {statistics && (
                <div className="text-sm text-purple-600">
                  {statistics.total_students} students ranked | Avg: {statistics.average_score}%
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Statistics Overview */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Average Score</div>
            <div className="text-2xl font-bold text-blue-600">
              {statistics.average_score}
            </div>
            <div className="text-xs text-gray-400">Class Average</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Highest Score</div>
            <div className="text-2xl font-bold text-green-600">
              {statistics.highest_score}
            </div>
            <div className="text-xs text-gray-400">Top Score</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Lowest Score</div>
            <div className="text-2xl font-bold text-red-600">
              {statistics.lowest_score}
            </div>
            <div className="text-xs text-gray-400">Needs Improvement</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Students Graded</div>
            <div className="text-2xl font-bold text-purple-600">
              {statistics.total_scored}/{statistics.total_students}
            </div>
            <div className="text-xs text-gray-400">Graded/Total</div>
          </div>
        </div>
      )}

      {/* Performance Analysis */}
      {performanceAnalysis && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Analysis</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Grade Distribution Chart */}
            <div className="lg:col-span-2">
              <div className="h-64">
                <ChartComponent
                  type="bar"
                  data={getGradeDistributionChartData()}
                  options={{
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                          display: true,
                          text: 'Percentage of Students'
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        display: false
                      }
                    }
                  }}
                />
              </div>
            </div>

            {/* Performance Insights */}
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Class Insights</h4>
              <ul className="space-y-2">
                {performanceAnalysis.analysis_insights && performanceAnalysis.analysis_insights.map((insight, index) => (
                  <li key={index} className="flex items-start text-sm text-gray-600">
                    <i className="fas fa-chart-line text-green-500 mt-1 mr-2"></i>
                    {insight}
                  </li>
                ))}
              </ul>

              {/* Top 3 Students */}
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-3">Top Performers</h4>
                {performanceAnalysis.top_students && performanceAnalysis.top_students.slice(0, 3).map((student, index) => (
                  <div key={index} className="flex items-center justify-between mb-2 p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
                        index === 0 ? 'bg-yellow-100 text-yellow-800' :
                        index === 1 ? 'bg-gray-100 text-gray-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {student.rank}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium">{student.name}</div>
                        <div className="text-xs text-gray-500">{student.reg_number}</div>
                      </div>
                    </div>
                    <div className="text-sm font-bold text-green-600">
                      {student.score}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rankings Table */}
      {rankings.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden print:shadow-none">
          <div className="p-6 border-b print:border-b-0">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">
                Student Rankings ({rankings.length} students)
              </h3>
              <div className="text-sm text-gray-500">
                <i className="fas fa-info-circle mr-1"></i>
                Same score = Same rank
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Percentage
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assessment Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rankings.map((student) => (
                  <tr 
                    key={`${student.student_reg}-${student.rank}`} 
                    className={`hover:bg-gray-50 ${getRankColor(student.rank)}`}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-full ${
                          student.rank === 1 ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                          student.rank === 2 ? 'bg-gray-100 text-gray-800 border border-gray-300' :
                          student.rank === 3 ? 'bg-orange-100 text-orange-800 border border-orange-300' :
                          'bg-blue-100 text-blue-800 border border-blue-300'
                        }`}>
                          <span className="font-bold">{student.rank}</span>
                          {student.rank <= 3 && (
                            <span className="ml-1">
                              {student.rank === 1 ? '🥇' : student.rank === 2 ? '🥈' : '🥉'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {student.student_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {student.student_reg}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {student.sex || 'Gender not specified'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-2xl font-bold text-gray-900">
                        {student.total_score}
                      </div>
                      <div className="text-xs text-gray-500">
                        out of 100
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getGradeColor(student.grade)}`}>
                        {student.grade}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-green-600 h-2.5 rounded-full" 
                            style={{ width: `${student.percentage}%` }}
                          ></div>
                        </div>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          {student.percentage}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => {
                          // Show assessment details modal
                          console.log('Show details for', student.student_reg);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-900"
                      >
                        <i className="fas fa-eye mr-1"></i>
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Legend:</span>
              <span className="mx-3">
                <span className="inline-block w-3 h-3 bg-yellow-400 rounded-full mr-1"></span>
                Rank 1
              </span>
              <span className="mx-3">
                <span className="inline-block w-3 h-3 bg-gray-400 rounded-full mr-1"></span>
                Rank 2
              </span>
              <span className="mx-3">
                <span className="inline-block w-3 h-3 bg-orange-400 rounded-full mr-1"></span>
                Rank 3
              </span>
              <span className="mx-3">
                <span className="inline-block w-3 h-3 bg-blue-400 rounded-full mr-1"></span>
                Top 10
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && rankings.length === 0 && selectedSubject && selectedClass && selectedSession && selectedTerm && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <i className="fas fa-trophy text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Rankings Available</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            No grades have been recorded for this subject and class combination yet.
            Please enter grades first to generate rankings.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubjectRankings;